/* eslint-disable @typescript-eslint/no-unsafe-return */
import * as glassEasel from 'glass-easel';
import { BaseBehaviorBuilder } from './base_behavior_builder';
import { ComponentType } from '../behavior';
import { ComponentProto } from '../component';
/**
 * A direct way to create a component
 */
export class ComponentBuilder extends BaseBehaviorBuilder {
    /** @internal */
    static create(codeSpace, is, alias) {
        const ret = new ComponentBuilder();
        const overallBehavior = codeSpace._$overallBehavior;
        ret._$codeSpace = codeSpace;
        ret._$ = codeSpace.getComponentSpace().defineWithMethodCaller(is || '');
        ret._$is = is || '';
        ret._$alias = alias;
        ret._$.methodCallerInit(function () {
            const originalCaller = this;
            let proto = ret._$proto;
            if (proto === undefined) {
                const methods = originalCaller.getComponentDefinition().behavior.getMethods();
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                proto = ret._$proto = new ComponentProto(methods, ret._$parents, ret._$export);
            }
            const caller = proto.derive();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            caller._$ = originalCaller;
            return caller;
        });
        if (overallBehavior)
            ret._$.behavior(overallBehavior);
        return ret;
    }
    /**
     * Set the component options
     *
     * If called multiple times, only the latest call is valid.
     */
    options(options) {
        this._$options = options;
        return this;
    }
    /** Use another behavior */
    behavior(behavior) {
        return super.behavior(behavior);
    }
    /** Set the export value when the component is being selected */
    export(f) {
        return super.export(f);
    }
    /**
     * Add some template data fields
     *
     * It does not support raw data, but a `gen` function which returns the new data fields.
     * The `gen` function executes once during component instance creation.
     */
    data(gen) {
        return super.data(gen);
    }
    /**
     * Add some template data fields
     *
     * The data should be JSON-compatible, and will be cloned during component creation.
     */
    staticData(data) {
        return super.staticData(data);
    }
    /**
     * Add a single property
     *
     * The property name should be different from other properties.
     */
    property(name, def) {
        return super.property(name, def);
    }
    /**
     * Add some public methods
     *
     * The public method can be used as an event handler, and can be visited in component instance.
     */
    methods(funcs) {
        return super.methods(funcs);
    }
    /**
     * Execute a function while component instance creation
     *
     * A `BuilderContext` is provided to tweak the component creation progress.
     * The return value is used as the "export" value of the behavior.
     */
    init(func) {
        return super.init(func);
    }
    /** Apply a classic definition object */
    definition(def) {
        super.definition(def);
        if (def.options)
            this.options(def.options);
        return this;
    }
    pageDefinition(def) {
        const customFields = Object.create(null);
        const compDef = {
            methods: {},
        };
        const keys = Object.keys(def);
        for (let i = 0; i < keys.length; i += 1) {
            const k = keys[i];
            if (k === 'data') {
                compDef.data = def.data;
            }
            else if (typeof def[k] === 'function') {
                ;
                compDef.methods[k] = def[k];
            }
            else if (k === 'methods') {
                customFields.methods = def.methods;
            }
            else {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                compDef[k] = def[k];
            }
        }
        this.definition(compDef);
        const keys2 = Object.keys(compDef);
        for (let i = 0; i < keys2.length; i += 1) {
            const k = keys2[i];
            if (k === 'data' || k === 'methods' || k === 'behaviors')
                continue;
            customFields[k] = compDef[k];
        }
        this._$.init(function () {
            Object.assign(this, glassEasel.dataUtils.simpleDeepCopy(customFields));
        });
        return this;
    }
    /**
     * Finish the component definition process
     */
    register() {
        var _a;
        const is = this._$is;
        const codeSpace = this._$codeSpace;
        // processing common fields
        const [options, styleIsolation] = codeSpace.prepareComponentOptions(is, this._$options);
        this._$.options(options);
        const staticConfig = codeSpace.getComponentStaticConfig(is);
        const using = staticConfig === null || staticConfig === void 0 ? void 0 : staticConfig.usingComponents;
        const generics = staticConfig === null || staticConfig === void 0 ? void 0 : staticConfig.componentGenerics;
        const placeholder = staticConfig === null || staticConfig === void 0 ? void 0 : staticConfig.componentPlaceholder;
        if (using)
            this._$.usingComponents(using);
        if (generics)
            this._$.generics(generics);
        if (placeholder)
            this._$.placeholders(placeholder);
        const template = codeSpace.getCompiledTemplate(is);
        if (template)
            this._$.template(template);
        // do registration
        codeSpace._$styleIsolationMap[is] = styleIsolation;
        const compDef = this._$.registerComponent();
        (_a = this._$alias) === null || _a === void 0 ? void 0 : _a.forEach((alias) => {
            this._$codeSpace.getComponentSpace().exportComponent(alias, this._$is);
        });
        return new ComponentType(compDef);
    }
    /**
     * Add extra this fields type
     */
    extraThisFieldsType() {
        return this;
    }
}
//# sourceMappingURL=component_builder.js.map